/**
 * Small color-coded badge identifying a deal's investment strategy.
 *
 * Used by:
 *   - KanbanCard (overlaid on the card thumbnail bottom-right)
 *   - PropertyCard pipeline pill (top-left, when the property is in a pipeline)
 *   - PropertyDetailModal hero (TBD Phase 3)
 *
 * Colors come from semantic Tailwind tokens (success/warning/info) so dark
 * mode handles itself; explicit hex from the mockup would defeat the theme.
 */
import {
  ArrowLeftRight,
  Home,
  Layers,
  RefreshCcw,
} from "lucide-react";

import { cn } from "@/lib/utils";
import type { Strategy } from "@/types/pipeline";

interface StrategyBadgeProps {
  /** A specific strategy, OR null to render a generic "In Pipeline" badge. */
  strategy: Strategy | null;
  className?: string;
  /** Optional icon-only variant for very tight cards. */
  iconOnly?: boolean;
}

const STRATEGY_META: Record<
  Strategy,
  { label: string; Icon: typeof Home; bg: string; ring: string }
> = {
  BRRRR: {
    label: "BRRRR",
    Icon: RefreshCcw,
    bg: "bg-success/85 text-white",
    ring: "ring-1 ring-success/40",
  },
  Flip: {
    label: "Flip",
    Icon: ArrowLeftRight,
    bg: "bg-warning/85 text-white",
    ring: "ring-1 ring-warning/40",
  },
  Rental: {
    label: "Rental",
    Icon: Home,
    bg: "bg-sky-500/85 text-white",
    ring: "ring-1 ring-sky-500/40",
  },
};

export function StrategyBadge({
  strategy,
  className,
  iconOnly,
}: StrategyBadgeProps) {
  const isMulti = strategy === null;
  const meta = strategy ? STRATEGY_META[strategy] : null;
  const Icon = meta?.Icon ?? Layers;
  const label = meta?.label ?? "In Pipeline";
  const bg = meta?.bg ?? "bg-primary/85 text-primary-foreground";
  const ring = meta?.ring ?? "ring-1 ring-primary/40";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide",
        bg,
        ring,
        className,
      )}
      aria-label={isMulti ? "Multiple strategies" : `${label} strategy`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {!iconOnly && label}
    </span>
  );
}
